import React, { useCallback, useEffect, useState } from 'react';
import { runnerApi, errorData, errorText, type NonIdempotentFinding, type RunDetail as RunDetailData } from '../../../api/migrationRunner';
import { Btn, Choice, Dialog, ErrorBox, Panel, Pill, RUN_LABEL, RUN_TONE, Spinner, bytes, duration, shortName, when } from './ui';

interface Props {
  rowId: number;
  onOpenBatch: (id: string) => void;
  onOpenConsole: (target: string, sql: string) => void;
  onChanged?: () => void;
}

const RESOLUTION_LABEL: Record<string, string> = {
  applied_by_hand: 'Resolved by hand',
  not_needed: 'Marked not needed',
  found_in_place: 'Found already in place (scan)',
  baseline: 'Start fresh baseline',
};

const STMT_CLASS: Record<string, string> = {
  applied: 'bg-green-50',
  skipped: 'bg-slate-50',
  failed: 'bg-red-50 shadow-[inset_4px_0_0_#dc2626]',
  not_run: 'bg-white',
  unknown: 'bg-white',
};

/**
 * One run of one migration on one database: statement by statement, the full
 * error, and what to do next (resume, re-run, resolve by hand, console).
 */
const RunDetail: React.FC<Props> = ({ rowId, onOpenBatch, onOpenConsole, onChanged }) => {
  const [data, setData] = useState<RunDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [override, setOverride] = useState(false);
  const [blockedBy, setBlockedBy] = useState<NonIdempotentFinding[] | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await runnerApi.run(rowId));
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load the run'));
    }
  }, [rowId]);

  useEffect(() => { load(); }, [load]);

  const startBatch = async (name: string, fn: () => Promise<{ id: string }>) => {
    setBusy(name);
    setError(null);
    try {
      const b = await fn();
      onChanged?.();
      onOpenBatch(b.id);
    } catch (err) {
      const d = errorData<{ findings?: NonIdempotentFinding[] }>(err);
      if (d?.code === 'NON_IDEMPOTENT' && d.findings) setBlockedBy(d.findings);
      else setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const act = async (name: string, fn: () => Promise<void>) => {
    setBusy(name);
    setError(null);
    try {
      await fn();
      await load();
      onChanged?.();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  if (!data) return error ? <ErrorBox>{error}</ErrorBox> : <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> Loading…</div>;

  const { run, statements } = data;
  const failed = statements.find((s) => s.status === 'failed');
  const counts = statements.reduce<Record<string, number>>((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});
  const open = ['failed', 'partial', 'interrupted'].includes(run.status);
  // Clean up: these states can be closed out too (never run again, rolled back, a run left "running").
  const closable = ['running', 'rolled_back', 'skipped', 'pending'].includes(run.status) && !!run.migration_id;
  const resumeFrom = data.resumeFrom;
  const unsafe = data.unsafeForRerun.length > 0;
  const company = run.company_name || (run.target_db === '__registry__' ? 'Registry' : run.target_db);
  const consoleSql = failed ? `-- opened from the failed ${shortName(run.folder_path || run.migration_name)} run, statement ${failed.index + 1}\n${failed.sql};` : '';

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={RUN_TONE[run.status] ?? 'mut'}>{RUN_LABEL[run.status] ?? run.status}</Pill>
        <b className="text-[15px] text-slate-900">{shortName(run.folder_path || run.migration_name)} · {company}</b>
        <span className="text-[13px] text-slate-500">by {run.executed_by} · {when(run.executed_at)}{run.batch_id ? ' · ' : ''}</span>
        {run.batch_id && <button type="button" className="text-[13px] font-semibold text-blue-600 hover:underline" onClick={() => onOpenBatch(run.batch_id!)}>batch</button>}
      </div>

      {run.status === 'resolved' && (
        <div className="flex flex-wrap items-start gap-3 rounded-xl border border-violet-200 bg-violet-50 px-4 py-3 text-[13px] text-violet-900">
          <div className="min-w-0 flex-1">
            <b>{RESOLUTION_LABEL[run.resolution ?? ''] ?? 'Resolved by hand'}</b> by {run.resolved_by} · {when(run.resolved_at)}
            <div className="mt-1 text-violet-800">"{run.resolved_note}"</div>
          </div>
          {run.mark_id && (
            <Btn kind="ghost" small disabled={!!busy} onClick={() => act('undo', async () => { await runnerApi.undoMark(run.mark_id!); })}>
              {busy === 'undo' ? 'Undoing…' : 'Undo this mark'}
            </Btn>
          )}
        </div>
      )}
      {error && (run.status === 'resolved' || (run.status === 'interrupted' && run.mark_id)) && <ErrorBox>{error}</ErrorBox>}
      {run.status === 'interrupted' && run.mark_id && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-[13px] text-amber-900">
          <span className="min-w-0 flex-1">Marked stopped by hand.</span>
          <Btn kind="ghost" small disabled={!!busy} onClick={() => act('undo', async () => { await runnerApi.undoMark(run.mark_id!); })}>Undo</Btn>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Panel
          title="Statement by statement"
          actions={
            <>
              {counts.applied ? <Pill tone="ok">{counts.applied} applied</Pill> : null}
              {counts.skipped ? <Pill tone="mut">{counts.skipped} skipped (earlier run)</Pill> : null}
              {counts.failed ? <Pill tone="bad">{counts.failed} failed</Pill> : null}
              {counts.not_run ? <Pill tone="mut">{counts.not_run} not run</Pill> : null}
            </>
          }
          bodyClass=""
        >
          {!data.statementHistory && (
            <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-800">
              Per-statement history starts once the runner's registry migration is applied; older runs only know the overall result.
            </div>
          )}
          <div className="max-h-[480px] divide-y divide-slate-100 overflow-auto">
            {statements.map((s) => (
              <div key={s.index} className={`grid grid-cols-[28px_1fr_auto] items-start gap-2 px-3 py-2 ${STMT_CLASS[s.status] ?? ''}`}>
                <span className="pt-0.5 text-right font-mono text-xs font-bold text-slate-400">{s.index + 1}</span>
                <pre className="min-w-0 whitespace-pre-wrap break-words font-mono text-[11.5px] text-slate-800">{s.sql}</pre>
                <span>
                  {s.status === 'applied' && <Pill tone="ok">{duration(s.durationMs)}</Pill>}
                  {s.status === 'failed' && <Pill tone="bad">failed</Pill>}
                  {s.status === 'not_run' && <Pill tone="mut">not run</Pill>}
                  {s.status === 'skipped' && <Pill tone="mut">skipped</Pill>}
                </span>
              </div>
            ))}
          </div>
          {(failed || run.error_message) && (
            <div className="border-t border-slate-200 p-4">
              <div className="mb-1.5 text-xs font-bold uppercase tracking-wide text-slate-500">
                Full error{(failed?.errorCode || run.error_code) ? ` · ${failed?.errorCode || run.error_code}` : ''}
                {(failed?.sqlState || run.sql_state) ? ` / SQLSTATE ${failed?.sqlState || run.sql_state}` : ''}
              </div>
              <ErrorBox>{failed?.errorMessage || run.error_message}</ErrorBox>
            </div>
          )}
        </Panel>

        <div className="space-y-4">
          {closable && (
            <Panel title="What now?">
              <div className="space-y-2.5 text-[13px]">
                {run.status === 'running' ? (
                  <div className="rounded-xl border-[1.5px] border-blue-600 bg-blue-50/60 px-3.5 py-3">
                    <b className="block text-slate-900">Mark stopped</b>
                    <span className="text-slate-600">If nothing is running on this database any more (no migration lock held, no heartbeat), this marks it interrupted so it can be resumed or resolved. Runs stuck like this for 30 minutes are marked on their own.</span>
                  </div>
                ) : (
                  <div className="rounded-xl border-[1.5px] border-slate-200 px-3.5 py-3">
                    <b className="block text-slate-900">{RUN_LABEL[run.status] ?? run.status}: it still shows as pending</b>
                    <span className="text-slate-600">Run it again, or record that it is done or not needed for this company.</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  {run.status === 'running' && (
                    <Btn disabled={!!busy} onClick={() => act('stop', async () => { await runnerApi.markStopped(rowId); })}>{busy === 'stop' ? 'Checking…' : 'Mark stopped'}</Btn>
                  )}
                  {run.status !== 'running' && (
                    <Btn kind="ghost" disabled={!!busy || (unsafe && !override)} onClick={() => startBatch('rerun', () => runnerApi.rerun(rowId, { allowNonIdempotent: override }))}>Re-run file</Btn>
                  )}
                  <Btn kind="ghost" disabled={!!busy} onClick={() => setResolveOpen(true)}>Resolve…</Btn>
                </div>
                {error && <ErrorBox>{error}</ErrorBox>}
              </div>
            </Panel>
          )}
          {open && (
            <Panel title="What now?">
              <div className="space-y-2.5 text-[13px]">
                {resumeFrom !== null && (
                  <div className="rounded-xl border-[1.5px] border-blue-600 bg-blue-50/60 px-3.5 py-3">
                    <b className="block text-slate-900">Resume from statement {resumeFrom + 1}</b>
                    <span className="text-slate-600">
                      Runs {resumeFrom + 1} to {statements.length} only.
                      {resumeFrom > 0 && ` 1–${resumeFrom} are already in.`} Fix the file first if statement {resumeFrom + 1} itself is wrong.
                    </span>
                  </div>
                )}
                <div className="rounded-xl border-[1.5px] border-slate-200 px-3.5 py-3">
                  <b className="block text-slate-900">Re-run the whole file</b>
                  <span className="text-slate-600">
                    {unsafe
                      ? `Only safe if every statement can run twice. Statement${data.unsafeForRerun.length === 1 ? '' : 's'} ${data.unsafeForRerun.slice(0, 5).map((u) => u.index + 1).join(', ')} can't (no IF NOT EXISTS), so this is blocked.`
                      : 'Every statement can run twice, so this is safe.'}
                  </span>
                  {unsafe && (
                    <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
                      <input type="checkbox" checked={override} onChange={(e) => setOverride(e.target.checked)} /> Re-run anyway (override)
                    </label>
                  )}
                </div>
                <div className="rounded-xl border-[1.5px] border-slate-200 px-3.5 py-3">
                  <b className="block text-slate-900">Resolve by hand</b>
                  <span className="text-slate-600">You fixed it yourself. Marks it resolved with your note and your name, never as a fake success.</span>
                </div>
                {failed && (
                  <div className="rounded-xl border-[1.5px] border-slate-200 px-3.5 py-3">
                    <b className="block text-slate-900">Open in query console</b>
                    <span className="text-slate-600">Pre-filled with this database and the failed statement.</span>
                  </div>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  {resumeFrom !== null && (
                    <Btn disabled={!!busy} onClick={() => startBatch('resume', () => runnerApi.resume(rowId, { fromIndex: resumeFrom }))}>
                      {busy === 'resume' ? 'Starting…' : `Resume from ${resumeFrom + 1}`}
                    </Btn>
                  )}
                  <Btn kind="ghost" disabled={!!busy || (unsafe && !override)} onClick={() => startBatch('rerun', () => runnerApi.rerun(rowId, { allowNonIdempotent: override }))}>
                    Re-run file
                  </Btn>
                  <Btn kind="ghost" disabled={!!busy} onClick={() => setResolveOpen(true)}>Resolve…</Btn>
                  {failed && <Btn kind="ghost" onClick={() => onOpenConsole(run.target_db, consoleSql)}>Open in console</Btn>}
                </div>
                {blockedBy && (
                  <ErrorBox>
                    Blocked: {blockedBy.flatMap((f) => f.statements.map((s) => `statement ${s.index + 1}: ${s.message}`)).slice(0, 4).join('\n')}
                  </ErrorBox>
                )}
                {error && <ErrorBox>{error}</ErrorBox>}
              </div>
            </Panel>
          )}
          <Panel title="Before this run">
            <div className="space-y-1 text-[13px] leading-relaxed text-slate-700">
              <div>
                Backup:{' '}
                {data.backup
                  ? <b>{run.target_db} · {when(data.backup.takenAt)} · {bytes(data.backup.sizeBytes)} ({data.backup.storage === 's3' ? 'S3' : 'this server'}{data.backup.verifiedAt ? ', verified' : ''})</b>
                  : <span className="text-slate-500">none taken</span>}
              </div>
              <div>Took {duration(run.duration_ms)}</div>
              <div className="text-slate-500">Ran on its own connection with a per-database lock and time limits; the connection is closed afterwards.</div>
            </div>
          </Panel>
        </div>
      </div>

      <ResolveDialog
        open={resolveOpen}
        onClose={() => setResolveOpen(false)}
        rowId={rowId}
        title={`Resolve ${shortName(run.folder_path || run.migration_name)} on ${company}`}
        appliedCount={counts.applied || 0}
        failedIndex={failed?.index ?? resumeFrom ?? null}
        total={statements.length}
        running={run.status === 'running'}
        state={RUN_LABEL[run.status] ?? run.status}
        onDone={async () => { setResolveOpen(false); await load(); onChanged?.(); }}
      />
    </div>
  );
};

const ResolveDialog: React.FC<{
  open: boolean; onClose: () => void; rowId: number; title: string;
  appliedCount: number; failedIndex: number | null; total: number; onDone: () => void;
  /** A run left "running": adds "Leave it, check again later" (= Mark stopped). */
  running?: boolean; state?: string;
}> = ({ open, onClose, rowId, title, appliedCount, failedIndex, total, onDone, running = false, state }) => {
  const [outcome, setOutcome] = useState<'applied_by_hand' | 'not_needed' | 'later'>('applied_by_hand');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (open) { setNote(''); setError(null); setOutcome('applied_by_hand'); } }, [open]);

  const rest = failedIndex !== null ? `${failedIndex + 1}–${total}` : 'the rest';
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (outcome === 'later') await runnerApi.markStopped(rowId);
      else await runnerApi.resolve(rowId, note.trim(), outcome);
      onDone();
    } catch (err) {
      setError(errorText(err, 'Could not resolve'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={busy || (outcome !== 'later' && note.trim().length < 5)}>{busy ? 'Saving…' : outcome === 'later' ? 'Mark stopped' : 'Mark resolved'}</Btn>
        </>
      }
    >
      <p>
        {state ? `Currently: ${state}. ` : ''}{appliedCount > 0 ? `Statements 1–${appliedCount} were applied by the run. ` : ''}What about {rest}?
      </p>
      <Choice name="resolve-outcome" checked={outcome === 'applied_by_hand'} onChange={() => setOutcome('applied_by_hand')} title="I applied the rest by hand" recommended>
        Marks {rest} as done by you.
      </Choice>
      <Choice name="resolve-outcome" checked={outcome === 'not_needed'} onChange={() => setOutcome('not_needed')} title="They're not needed for this company">
        Skipped on purpose; the grid stops flagging it.
      </Choice>
      {running && (
        <Choice name="resolve-outcome" checked={outcome === 'later'} onChange={() => setOutcome('later')} title="Leave it, check again later">
          Clears "stuck" (marks it stopped) so it can be resumed or re-run.
        </Choice>
      )}
      {outcome !== 'later' && <label className="block">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Note (required)</span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          placeholder="What did you do? e.g. Fixed the AFTER clause and ran 3–5 in the console."
        />
      </label>}
      <p className="text-xs text-slate-500">Shown in purple in the grid and in history with your name. It is never recorded as a success. Undo it from the run or History for 30 days.</p>
      {error && <ErrorBox>{error}</ErrorBox>}
    </Dialog>
  );
};

export default RunDetail;
